"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";

import { Loader } from "@/dashboard/components/ui/loader";
import { DASHBOARD_ROUTES } from "@/dashboard/constants/routes";
import { useAuth } from "@/dashboard/contexts/auth-context";

// Only used to resolve a relative value; any origin other than this one is a rejected target.
const PLACEHOLDER_ORIGIN = "http://dashboard.invalid";

/**
 * Return the value as a same-origin path (path + query + hash), or null when it would leave this
 * site. Resolved twice, the way a browser would: "/\evil.example" and "/.//evil.example" both
 * become "//evil.example".
 */
function toSameOriginPathOrNull(raw: string | null | undefined): string | null {
  if (!raw || /^[a-z][a-z\d+.-]*:/i.test(raw)) {
    return null;
  }
  const relative = raw.startsWith("/") || raw.startsWith("\\") ? raw : `/${raw}`;
  try {
    const url = new URL(relative, PLACEHOLDER_ORIGIN);
    const path = url.pathname + url.search + url.hash;
    const isSameOrigin =
      url.origin === PLACEHOLDER_ORIGIN &&
      new URL(path, PLACEHOLDER_ORIGIN).origin === PLACEHOLDER_ORIGIN;
    return isSameOrigin ? path : null;
  } catch {
    return null;
  }
}

function safeRedirect(
  raw: string | null | undefined,
  opts: { isSelfServe: boolean; isNonDashboardUser: boolean; isAnalyticsViewer: boolean }
): string {
  const path = toSameOriginPathOrNull(raw);
  if (path) return path;
  if (opts.isSelfServe) return DASHBOARD_ROUTES.CALL_SCORING;
  if (opts.isNonDashboardUser) return DASHBOARD_ROUTES.GREENHOUSE_QUEUE;
  if (opts.isAnalyticsViewer) return DASHBOARD_ROUTES.ANALYTICS_INBOUND;
  return DASHBOARD_ROUTES.HUB_FLAGGED;
}

export function withGuestPage<P extends object>(
  Component: React.ComponentType<P>,
  options?: { redirectTo?: string }
): React.FC<P> {
  return function GuestProtectedPage(props: P) {
    const { user, authResolved, isSelfServe, isNonDashboardUser, isAnalyticsViewer } = useAuth();
    const searchParams = useSearchParams();

    /* -----------------------------------------------
     * Compute target *once* so it never flip-flops
     * --------------------------------------------- */
    const redirectTarget = useMemo(() => {
      if (options?.redirectTo) {
        return options.redirectTo;
      }

      const qp = searchParams?.get("redirectTo");
      return safeRedirect(qp, { isSelfServe, isNonDashboardUser, isAnalyticsViewer });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelfServe, isNonDashboardUser, isAnalyticsViewer]);

    useEffect(() => {
      if (authResolved && user) {
        // A full load, not router.replace: Next 16's router crashes (React #310) when a
        // client navigation lands on a page that server-redirects, as /dashboard/hub does.
        window.location.replace(redirectTarget);
      }
    }, [authResolved, user, redirectTarget]);

    /* --------------- loading / blocking --------------- */
    if (!authResolved) {
      return <Loader variant="fullscreen" />;
    }

    if (user) {
      return null;
    }

    return <Component {...props} />;
  };
}
